import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, color, display, mb, mt, mv, onDesktop, p, w100} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {FieldWrapper} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Radio} from "@pg-design/radio-module";
import {Text} from "@pg-design/text-module";
import {FormikForm, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {noop} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {validationMessages} from "../../../form/utils/validation_messages";
import {fetchAllSearchLists} from "../../../search/actions/fetch_search_all_action";
import {SearchFiltersRooms} from "../../../search/components/filters/SearchFiltersRooms";
import {SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdown";
import {IRootState} from "../../../store/store";
import {selectUserProfileData} from "../../../user/utils/select_user_profile_data";
import {subscribeToOfferNotification} from "../../api/subsribe_to_offer_notification";
import {
    IOfferNotificationChatBotFormValues,
    offerChatBotFormValidationSchema,
    offerNotificationChatBotFormInitialValues,
    PhoneType
} from "../../constants/offer_notification_chatbot_form";
import {hitGtmAddToCompareClick} from "../../tracking/whatsapp_gtm_events";
import {OfferNotificationVariant} from "../../types/OfferNotificationVariant";

interface IProps {
    onAfterSubmit?: () => void;
    className?: string;
}

export const OfferChatBotForm = (props: IProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const userUuid = useSelector((store: IRootState) => selectUserProfileData(store)?.uuid);

    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const dispatchFetchAllSearchLists = async (searchInput: string) => {
        // fetch search lists for chatbot
        const searchContentParams = {
            googlePOIs: false,
            regions: true,
            offers: false,
            vendors: false
        };
        return dispatch(fetchAllSearchLists(searchInput, {searchContent: searchContentParams}));
    };

    const onSubmit: IFormikSubmitFn<IOfferNotificationChatBotFormValues> = (values) => {
        subscribeToOfferNotification(values, OfferNotificationVariant.OFFER, userUuid);
        hitGtmAddToCompareClick(values.phone_type);
        props.onAfterSubmit?.();
    };

    return (
        <div className={props.className}>
            <Text variant="body_copy_2" css={[mb(2)]}>
                Otrzymuj na WhatsApp lub SMS nowe oferty zgodne z Twoimi preferencjami.
            </Text>

            <FormikForm
                initialValues={offerNotificationChatBotFormInitialValues}
                validationSchema={offerChatBotFormValidationSchema}
                onSubmit={onSubmit}
                validateOnBlur={false}
                validateOnChange={false}
            >
                {({values, getFieldProps, setFieldValue, errors}) => (
                    <>
                        <div css={searchWrapperStyle}>
                            <SearchAutocomplete
                                values={values}
                                onChange={(newValues) => {
                                    setFieldValue("search", newValues.search);
                                }}
                                onAfterChange={noop}
                                onDropdownStatusChange={noop}
                                onAfterTabSwitch={noop}
                                fetchAllSearchLists={dispatchFetchAllSearchLists}
                                inputIcon={<MapMarkerOutlineIcon size="2.4" />}
                                placeholder="Dodaj..."
                                addedRegionPlaceholder={() => "Dodaj..."}
                                inputContainerCss={searchInputContainerStyle}
                                inputWrapperCss={inputWrapperStyle}
                                disableMultiRegion
                                disableExtraMargins
                                regionPillWrapperCss={display("block")}
                                errors={errors.search ? {search: [validationMessages.required], distance: []} : undefined}
                                renderDropdownList={(props) => {
                                    return (
                                        <SearchAutocompleteDropdown
                                            dropdownIsOpen={props.dropdownIsOpen}
                                            search={props.search}
                                            selectedTab={props.selectedTab}
                                            onLinkClick={props.onLinkClick}
                                            requestStateObj={props.requestStateObj}
                                            activeItem={props.activeItem}
                                            offers={props.offers}
                                            places={props.places}
                                            regions={props.regions}
                                            removeRegionTag={props.removeRegionTag}
                                            vendors={props.vendors}
                                            getFieldProps={props.getFieldProps}
                                        />
                                    );
                                }}
                            />
                        </div>

                        <Text as="div" variant="info_txt_1" css={[mt(2), mb(0.5)]}>
                            Liczba pokoi
                        </Text>
                        <SearchFiltersRooms
                            toggleDropdown={() => null}
                            clearField={() => null}
                            name="rooms"
                            value={values.rooms}
                            showFooter={false}
                            onChange={setFieldValue}
                            singleChoice
                            error={errors?.rooms ? [validationMessages.required] : undefined}
                        />

                        <div css={formSecondColumnStyle}>
                            <div css={radioWrapperStyle}>
                                <Radio
                                    name="phone_type"
                                    id="phone_type_sms"
                                    value={PhoneType.Sms}
                                    isActive={getFieldProps("phone_type").value === PhoneType.Sms}
                                    onChange={() => setFieldValue("phone_type", PhoneType.Sms)}
                                    labelContent="SMS"
                                />
                                <Radio
                                    name="phone_type"
                                    id="phone_type_whatsapp"
                                    value={PhoneType.Whatsapp}
                                    isActive={getFieldProps("phone_type").value === PhoneType.Whatsapp}
                                    onChange={() => setFieldValue("phone_type", PhoneType.Whatsapp)}
                                    labelContent="WhatsApp"
                                />
                            </div>

                            <FormikForm.PhoneField codes={countryCodes} name="phone" placeholder="Twój telefon" css={[mt(2), mb(1)]} maxMenuHeight={100} />

                            <Text variant="info_txt_3" css={[mb(2), color(theme.colors.gray[800])]}>
                                Administratorem Twoich danych osobowych jest:
                                <br />
                                Property Group sp. z o.o.{" "}
                                <Link href={rpAppLink.rodoTerms.base()} target="_blank" size="inherit" underline css={color(theme.colors.gray[800])}>
                                    dowiedz się więcej o przetwarzaniu Twoich danych.
                                </Link>{" "}
                            </Text>

                            <FieldWrapper
                                css={[mb(2)]}
                                fieldState={errors.general_marketing_consent ? "error" : "default"}
                                message={errors.general_marketing_consent}
                            >
                                <FormikForm.Checkbox
                                    name="general_marketing_consent"
                                    checkedMarkColor="#02D054"
                                    id="general_marketing_consent"
                                    labelContent={
                                        <Text variant="info_txt_1" as="span">
                                            Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.
                                        </Text>
                                    }
                                />
                            </FieldWrapper>

                            <Button type="submit" variant="filled_primary" size="small" css={[w100]}>
                                Zapisz się
                            </Button>

                            <Text variant="info_txt_2" css={[mv(1)]} align="center">
                                akceptuję{" "}
                                <Link href={rpAppLink.termsOfService.base()} target="_blank" size="inherit" underline>
                                    regulamin serwisu
                                </Link>
                            </Text>
                        </div>
                    </>
                )}
            </FormikForm>
        </div>
    );
};

const formSecondColumnStyle = (theme: Theme) => css`
    ${borderRadius()};
    ${p(1)};
    background-color: ${theme.colors.gray[300]};
`;

const radioWrapperStyle = css`
    display: flex;
    flex-direction: row;
    ${p(1, 0)};
    gap: ${calculateRemSize(4)};
`;

const searchWrapperStyle = css`
    position: relative;
    z-index: 10001; // phone input overlay
`;

const inputWrapperStyle = css`
    margin: 0;
`;

const searchInputContainerStyle = css`
    ${borderRadius()};

    ${onDesktop(css`
        ${borderRadius()}; // it is necessary to override desktop styles
    `)};
`;
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, color, display, mb, mt, mv, onDesktop, p, w100} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {FieldWrapper} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Radio} from "@pg-design/radio-module";
import {Text} from "@pg-design/text-module";
import {FormikForm, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {noop} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {validationMessages} from "../../../form/utils/validation_messages";
import {fetchAllSearchLists} from "../../../search/actions/fetch_search_all_action";
import {SearchFiltersRooms} from "../../../search/components/filters/SearchFiltersRooms";
import {SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdown";
import {IRootState} from "../../../store/store";
import {selectUserProfileData} from "../../../user/utils/select_user_profile_data";
import {subscribeToOfferNotification} from "../../api/subsribe_to_offer_notification";
import {
    IOfferNotificationChatBotFormValues,
    offerChatBotFormValidationSchema,
    offerNotificationChatBotFormInitialValues,
    PhoneType
} from "../../constants/offer_notification_chatbot_form";
import {hitGtmAddToCompareClick} from "../../tracking/whatsapp_gtm_events";
import {OfferNotificationVariant} from "../../types/OfferNotificationVariant";

interface IProps {
    onAfterSubmit?: () => void;
    className?: string;
}

export const OfferChatBotForm = (props: IProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const userUuid = useSelector((store: IRootState) => selectUserProfileData(store)?.uuid);

    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const dispatchFetchAllSearchLists = async (searchInput: string) => {
        // fetch search lists for chatbot
        const searchContentParams = {
            googlePOIs: false,
            regions: true,
            offers: false,
            vendors: false
        };
        return dispatch(fetchAllSearchLists(searchInput, {searchContent: searchContentParams}));
    };

    const onSubmit: IFormikSubmitFn<IOfferNotificationChatBotFormValues> = (values) => {
        subscribeToOfferNotification(values, OfferNotificationVariant.OFFER, userUuid);
        hitGtmAddToCompareClick(values.phone_type);
        props.onAfterSubmit?.();
    };

    return (
        <div className={props.className}>
            <Text variant="body_copy_2" css={[mb(2)]}>
                Otrzymuj na WhatsApp lub SMS nowe oferty zgodne z Twoimi preferencjami.
            </Text>

            <FormikForm
                initialValues={offerNotificationChatBotFormInitialValues}
                validationSchema={offerChatBotFormValidationSchema}
                onSubmit={onSubmit}
                validateOnBlur={false}
                validateOnChange={false}
            >
                {({values, getFieldProps, setFieldValue, errors}) => (
                    <>
                        <div css={searchWrapperStyle}>
                            <SearchAutocomplete
                                values={values}
                                onChange={(newValues) => {
                                    setFieldValue("search", newValues.search);
                                }}
                                onAfterChange={noop}
                                onDropdownStatusChange={noop}
                                onAfterTabSwitch={noop}
                                fetchAllSearchLists={dispatchFetchAllSearchLists}
                                inputIcon={<MapMarkerOutlineIcon size="2.4" />}
                                placeholder="Dodaj..."
                                addedRegionPlaceholder={() => "Dodaj..."}
                                inputContainerCss={searchInputContainerStyle}
                                inputWrapperCss={inputWrapperStyle}
                                disableMultiRegion
                                disableExtraMargins
                                regionPillWrapperCss={display("block")}
                                errors={errors.search ? {search: [validationMessages.required], distance: []} : undefined}
                                renderDropdownList={(props) => {
                                    return (
                                        <SearchAutocompleteDropdown
                                            dropdownIsOpen={props.dropdownIsOpen}
                                            search={props.search}
                                            selectedTab={props.selectedTab}
                                            onLinkClick={props.onLinkClick}
                                            requestStateObj={props.requestStateObj}
                                            activeItem={props.activeItem}
                                            offers={props.offers}
                                            places={props.places}
                                            regions={props.regions}
                                            removeRegionTag={props.removeRegionTag}
                                            vendors={props.vendors}
                                            getFieldProps={props.getFieldProps}
                                        />
                                    );
                                }}
                            />
                        </div>

                        <Text as="div" variant="info_txt_1" css={[mt(2), mb(0.5)]}>
                            Liczba pokoi
                        </Text>
                        <SearchFiltersRooms
                            toggleDropdown={() => null}
                            clearField={() => null}
                            name="rooms"
                            value={values.rooms}
                            showFooter={false}
                            onChange={setFieldValue}
                            singleChoice
                            error={errors?.rooms ? [validationMessages.required] : undefined}
                        />

                        <div css={formSecondColumnStyle}>
                            <div css={radioWrapperStyle}>
                                <Radio
                                    name="phone_type"
                                    id="phone_type_sms"
                                    value={PhoneType.Sms}
                                    isActive={getFieldProps("phone_type").value === PhoneType.Sms}
                                    onChange={() => setFieldValue("phone_type", PhoneType.Sms)}
                                    labelContent="SMS"
                                />
                                <Radio
                                    name="phone_type"
                                    id="phone_type_whatsapp"
                                    value={PhoneType.Whatsapp}
                                    isActive={getFieldProps("phone_type").value === PhoneType.Whatsapp}
                                    onChange={() => setFieldValue("phone_type", PhoneType.Whatsapp)}
                                    labelContent="WhatsApp"
                                />
                            </div>

                            <FormikForm.PhoneField codes={countryCodes} name="phone" placeholder="Twój telefon" css={[mt(2), mb(1)]} maxMenuHeight={100} />

                            <Text variant="info_txt_3" css={[mb(2), color(theme.colors.gray[800])]}>
                                Administratorem Twoich danych osobowych jest:
                                <br />
                                Property Group sp. z o.o.{" "}
                                <Link href={rpAppLink.rodoTerms.base()} target="_blank" size="inherit" underline css={color(theme.colors.gray[800])}>
                                    dowiedz się więcej o przetwarzaniu Twoich danych.
                                </Link>{" "}
                            </Text>

                            <FieldWrapper
                                css={[mb(2)]}
                                fieldState={errors.general_marketing_consent ? "error" : "default"}
                                message={errors.general_marketing_consent}
                            >
                                <FormikForm.Checkbox
                                    name="general_marketing_consent"
                                    checkedMarkColor="#02D054"
                                    id="general_marketing_consent"
                                    labelContent={
                                        <Text variant="info_txt_1" as="span">
                                            Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.
                                        </Text>
                                    }
                                />
                            </FieldWrapper>

                            <Button type="submit" variant="filled_primary" size="small" css={[w100]}>
                                Zapisz się
                            </Button>

                            <Text variant="info_txt_2" css={[mv(1)]} align="center">
                                akceptuję{" "}
                                <Link href={rpAppLink.termsOfService.base()} target="_blank" size="inherit" underline>
                                    regulamin serwisu
                                </Link>
                            </Text>
                        </div>
                    </>
                )}
            </FormikForm>
        </div>
    );
};

const formSecondColumnStyle = (theme: Theme) => css`
    ${borderRadius()};
    ${p(1)};
    background-color: ${theme.colors.gray[300]};
`;

const radioWrapperStyle = css`
    display: flex;
    flex-direction: row;
    ${p(1, 0)};
    gap: ${calculateRemSize(4)};
`;

const searchWrapperStyle = css`
    position: relative;
    z-index: 10001; // phone input overlay
`;

const inputWrapperStyle = css`
    margin: 0;
`;

const searchInputContainerStyle = css`
    ${borderRadius()};

    ${onDesktop(css`
        ${borderRadius()}; // it is necessary to override desktop styles
    `)};
`;
